import { useCallback, useContext, useEffect } from 'react'
import { CrmAccessTokenPayloadDto, parseJwt } from '@sparling/financial-common'
import { useDispatch } from 'react-redux'
import { AppStateContext } from './AppStateContext'
import { setAuthToken, unsetAuthToken } from '../../store/actions'

interface AuthResult {
  setAuthAccessToken: (token: string | undefined) => void
  removeAuthAccessToken: () => void
  isUserLoggedIn: boolean
  setAccessTokenData: (value: CrmAccessTokenPayloadDto | undefined) => void
  getAccessTokenData: () => CrmAccessTokenPayloadDto
  getAccessTokenDataFromAuthToken: (token: string | undefined) => CrmAccessTokenPayloadDto | undefined
  removeAccessTokenData: () => void
}

export const useAuth: () => AuthResult = () => {
  const {
    setAuthTokenInStore,
    removeAuthTokenFromStore,
    isLoggedIn,
    getAuthToken,
    setAuthAccessTokenPayloadInStore,
    getAuthAccessTokenPayloadInStore,
    removeAuthAccessTokenPayloadFromStore,
  } = useContext(AppStateContext)
  const dispatch = useDispatch()

  useEffect(() => {
    const token = getAuthToken()
    if (token) {
      dispatch(setAuthToken(token))
    }
  }, [dispatch, getAuthToken])

  const setAuthAccessToken = useCallback(
    (token: string | undefined) => {
      dispatch(setAuthToken(token as string))
      return setAuthTokenInStore(token)
    },
    [dispatch, setAuthTokenInStore],
  )

  const removeAuthAccessToken = useCallback(() => {
    dispatch(unsetAuthToken())
    return removeAuthTokenFromStore()
  }, [dispatch, removeAuthTokenFromStore])

  const isUserLoggedIn = isLoggedIn

  const getAccessTokenData = useCallback(() => getAuthAccessTokenPayloadInStore(), [getAuthAccessTokenPayloadInStore])

  const setAccessTokenData = useCallback(
    (value: CrmAccessTokenPayloadDto | undefined) => setAuthAccessTokenPayloadInStore(value),
    [setAuthAccessTokenPayloadInStore],
  )

  const getAccessTokenDataFromAuthToken = (token: string | undefined): CrmAccessTokenPayloadDto | undefined => {
    if (token) {
      return <CrmAccessTokenPayloadDto>parseJwt(token)
    }

    return undefined
  }

  const removeAccessTokenData = useCallback(
    () => removeAuthAccessTokenPayloadFromStore(),
    [removeAuthAccessTokenPayloadFromStore],
  )

  return {
    setAuthAccessToken,
    removeAuthAccessToken,
    isUserLoggedIn,
    setAccessTokenData,
    getAccessTokenData,
    getAccessTokenDataFromAuthToken,
    removeAccessTokenData,
  }
}
